import { useEffect, useRef, useState } from 'react';
import { CircularProgress } from '@material-ui/core';
import cx from 'classnames';

import { CustomTypography, TYPOGRAPHY_VARIANT } from 'src/components/CustomTypography';
import { RESOURCE_CHANNELS } from 'src/data/channel';
import {
  flatConnectedAccounts,
  getPerformanceLoaderLabel,
  isAccountInProgress,
  PERFORMANCE_EXTRACTION_STATUS,
} from 'src/data/performance';
import { useChannelAccounts, useErrorHandler, useGetAccountsLastExtraction } from 'src/services';
import { useCurrentCustomerId, useCurrentWorkspaceId } from 'src/store';

import { useStyles } from './useStyles';
import { useSubscribeForPubsub } from './useSubscribeForPubsub';
import { useViewState } from './useViewState';

export const DataExtractionIndicator = () => {
  const styles = useStyles();

  const currentCustomerId = useCurrentCustomerId();
  const currentWorkspaceId = useCurrentWorkspaceId();
  const handleError = useErrorHandler();

  const { completionPercentMap, runningCustomModels } = useViewState();

  const { connectedAccounts } = useChannelAccounts();

  const accountsInProgress = flatConnectedAccounts(connectedAccounts, true).filter(
    (account) => isAccountInProgress(account) && !RESOURCE_CHANNELS.includes(account.channel)
  );

  const getTitle = () => {
    const lastAccountInProgress = accountsInProgress[accountsInProgress.length - 1];
    const progressPercent = lastAccountInProgress
      ? completionPercentMap[lastAccountInProgress.channel]?.[lastAccountInProgress.id]
      : null;

    return getPerformanceLoaderLabel({
      channel: accountsInProgress[0]?.channel,
      progressPercent,
      isTrainingModel: !accountsInProgress.length && runningCustomModels.length,
    });
  };

  const accountsInProgressRef = useRef([]);
  const lastAccountInProgressRef = useRef(null);
  const [isInsightsPanelOpen, setIsInsightsPanelOpen] = useState(false);

  useEffect(() => {
    if (
      !(accountsInProgress.length || accountsInProgressRef.current.length) ||
      !currentCustomerId
    ) {
      return;
    }
    lastAccountInProgressRef.current = accountsInProgressRef.current.find(
      (accountInProgress) => !accountsInProgress.includes(accountInProgress)
    );

    const isLastAccountExtractionDone =
      lastAccountInProgressRef.current?.status === PERFORMANCE_EXTRACTION_STATUS.connected;

    if (lastAccountInProgressRef.current && isLastAccountExtractionDone) {
      accountsInProgressRef.current = accountsInProgressRef.current.filter(
        (prevAccountInProgress) => prevAccountInProgress !== lastAccountInProgressRef.current
      );
    }
    if (accountsInProgress.length) {
      // we don't want to override accountsInProgressRef if accountsInProgress is empty
      accountsInProgressRef.current = accountsInProgress;
    }
  }, [
    currentWorkspaceId,
    currentCustomerId,
    accountsInProgressRef,
    accountsInProgress,
    handleError,
  ]);

  useSubscribeForPubsub(setIsInsightsPanelOpen);
  useGetAccountsLastExtraction();

  if (!currentCustomerId || (!accountsInProgress.length && !runningCustomModels.length)) {
    return null;
  }

  return (
    <div
      className={cx(styles.root, {
        [styles.isInsightsPanelOpen]: isInsightsPanelOpen,
      })}
      data-testid="DataExtractionIndicator"
    >
      <CircularProgress
        classes={{ indeterminate: styles.loader }}
        size={16}
      />
      <CustomTypography
        className={styles.title}
        variant={TYPOGRAPHY_VARIANT.regular14}
      >
        {getTitle()}
      </CustomTypography>
    </div>
  );
};
